import { Checkbox, Table, TableHead, TableRow, TableCell, TableBody, Card, IconButton, Typography, Tooltip } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { CashflowDto, isCashflowDirectionAllowedForGroup, ImportedPayorderDto, ImportedPayorderProps, PayOrderGroup, PayorderImportReportDto, RunImportDto, UpdateImportedPayorderDto } from '@sr/dto';
import { useUpdateSelected as useUpdateSelectedState } from 'hooks/payorders-import.hook';
import { useRef, useState } from 'react';
import { MoreMenu, useMoreMenu } from 'shared/ui/more-menu';
import { formatDate } from 'utils/date-format-helpers';
import { useCashflowsDropdown } from 'hooks/cashflows.hook';
import { IRemoteCollection } from 'utils/remote-collection.hook';
import { useSnack } from 'shared/ui/snack';
import PriceSpan from 'shared/ui/price-span';
import { RowStatusInfoCell } from './cell-status';
import { InvoiceCellContent } from './cell-invoices';
import { CashflowCellContent } from './cell-cashflow';
import { GroupCellContent } from './cell-group';
import { green, grey, red, yellow } from '@mui/material/colors';
import { LinkToPayOrder } from 'shared/ui/links/link-to-payorder';
import KeyboardDoubleArrowDownIcon from '@mui/icons-material/KeyboardDoubleArrowDown';
import { postReq } from 'shared/api/axios';
import { extractNestJsErrorMessage } from 'shared/api/rtk-query';
import { useStateSelector } from 'shared/store';

const columns = [
  'Дата / Номер документа',
  'Плательщик',
  'Получатель',
  'Группа',
  'Назначение платежа',
  'Счет',
  'Статья движения средств',
  'Сумма'
];

const statusColors = {
  success: green[200],
  warning: yellow[200],
  red: red[200],
  grey: grey[200]
};


type Props = {
  sessionId: number,
  rows: ImportedPayorderDto[],
  reload: () => void,
  onUpdateRow: (data: UpdateImportedPayorderDto) => Promise<void>,
};

export const ImportedPayordersList = ({ sessionId, rows, reload, onUpdateRow }: Props) => {  

  const { showError } = useSnack();
  const { updateAllState } = useUpdateSelectedState();

  const cashflowsDropDownList = useCashflowsDropdown();

  const anchorRef = useRef<HTMLButtonElement>(null);

  const { openMenu, menuData } = useMoreMenu([{
    name: 'Выделить всё',
    icon: <CheckBoxIcon />,
    onClick: () => updateAllState(sessionId, true).then(reload).catch(e => showError('Ошибка: ' + e.message))
  },
  {
    name: 'Снять всё',
    icon: <CheckBoxOutlineBlankIcon />,
    onClick: () => updateAllState(sessionId, false).then(reload).catch(e => showError('Ошибка: ' + e.message))
  }]);

  const handleSelectAllClick = () => {
    openMenu(anchorRef.current!);
  };

  return (
    <>
      <Card>
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell align='center' width="40px">
                <IconButton
                  color='primary'
                  ref={anchorRef}
                  onClick={handleSelectAllClick}
                >
                  <ArrowDropDownIcon />
                </IconButton>
              </TableCell>
              {columns.map((column, idx) => <TableCell key={idx}>{column}</TableCell>)}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(row =>
              <ImportedRow
                key={row.id}
                importedPayorder={row}
                onChange={onUpdateRow}
                cashflowsDropDownList={cashflowsDropDownList}
              />)}
          </TableBody>
        </Table>

      </Card>

      <MoreMenu {...menuData} />
    </>
  );
};


type RowProps = {
  importedPayorder: ImportedPayorderDto;
  onChange: (data: UpdateImportedPayorderDto) => Promise<void>;
  cashflowsDropDownList: IRemoteCollection<CashflowDto>;
};

const ImportedRow = ({ importedPayorder, onChange, cashflowsDropDownList }: RowProps) => {
  const isRowValid = !importedPayorder.invalidReasons;
  const invalidReasons = importedPayorder.invalidReasons;
  const duplicate = invalidReasons?.hasDuplicate ? importedPayorder.duplicate : undefined;
  const draftMatch = !duplicate ? importedPayorder.draftMatch : undefined;
  const { bankAccountId } = useStateSelector((state) => state.ui.payorderImport);
  const { runImportSingle } = useRunImportSingle();
  const { showWarning } = useSnack();


  const [disabled, setDisabled] = useState(false);

  const handleCheckToggle = async (_: any, checked: boolean) => {
    setDisabled(true);
    const newData: UpdateImportedPayorderDto = {
      id: importedPayorder.id,
      ...importedPayorder.importProps,
      selected: checked
    };
    await onChange(newData);
    setDisabled(false);
  };

  const handleInvoiceChange = async (invoiceId: number | null) => {
    setDisabled(true);
    const newData: UpdateImportedPayorderDto = {
      id: importedPayorder.id,
      ...importedPayorder.importProps,
      selectedInvoiceId: invoiceId
    };

    await onChange(newData);
    setDisabled(false);
  };

  const handleCashflowChange = async (cashflowId: number | null) => {
    setDisabled(true);
    const newData: UpdateImportedPayorderDto = {
      id: importedPayorder.id,
      ...importedPayorder.importProps,
      selectedCashflowId: cashflowId
    };

    await onChange(newData);
    setDisabled(false);
  };

  const isSelectedCashflowAllowed = (group: PayOrderGroup | null) => {
    const cashflowId = importedPayorder.importProps.selectedCashflowId;
    if (!cashflowId || !group) return true;
    const cashflow = cashflowsDropDownList.items.find((c) => c.id === cashflowId);
    return !cashflow || isCashflowDirectionAllowedForGroup(cashflow.direction, group);
  };

  const handleGroupChange = async (group: PayOrderGroup | null) => {
    setDisabled(true);
    const newData: UpdateImportedPayorderDto = {
      id: importedPayorder.id,
      ...importedPayorder.importProps,
      selectedGroup: group,
      // сбрасываем статью, если её направление не подходит новой группе
      selectedCashflowId: isSelectedCashflowAllowed(group) ? importedPayorder.importProps.selectedCashflowId : null
    };

    await onChange(newData);
    setDisabled(false);
  };

  const handleRunImportSingle = async () => {
    if (!bankAccountId) {
      showWarning('Выберите банковский счет для импорта');
      return;
    }
    await runImportSingle(importedPayorder.id, bankAccountId);
  };

  const isDisabled = (props: ImportedPayorderProps) =>
    !importedPayorder.isTransfer && !(props.selectedCashflowId && props.selectedGroup && props.selectedInvoiceId);

  const bgcolor = getRowBgColor(importedPayorder);  

  return (
    <TableRow sx={{ bgcolor } }>
      <TableCell align='center' width="40px">
        {isRowValid
          ?
          <>
            <Checkbox
              checked={importedPayorder.importProps.selected}
              disabled={isDisabled(importedPayorder.importProps) || disabled}
              onChange={handleCheckToggle} />
            <IconButton color="primary" sx={{ border: 1 }} onClick={handleRunImportSingle}> <KeyboardDoubleArrowDownIcon sx={{m: -1}}/> </IconButton>
          </>
          :
          <RowStatusInfoCell reasons={importedPayorder.invalidReasons} />
        }
      </TableCell>
      <TableCell>
        <div> {formatDate(importedPayorder.paymentDate)} </div>
        <div>
          {duplicate
            ? <LinkToPayOrder payorder={duplicate} children={importedPayorder.documentNumber}/>
            : draftMatch
              ? <LinkToPayOrder payorder={draftMatch} children={importedPayorder.documentNumber}/>
              : importedPayorder.documentNumber}
        </div>
        {draftMatch &&
          <Typography variant="inherit" color="info.main">
            будет обновлён черновик №<LinkToPayOrder payorder={draftMatch}/>
          </Typography>}
        {importedPayorder.hasMultipleDraftMatches &&
          <Typography variant="inherit" color="warning.main">
            найдено несколько черновиков-кандидатов
          </Typography>}
      </TableCell>
      <TableCell>
        <div>{importedPayorder.payerName}</div>
        <Typography variant="inherit" color="grey.600">инн: {importedPayorder.payerInn}</Typography>
        <Typography variant="inherit" color="grey.600">кпп: {importedPayorder.payerKpp}</Typography>

      </TableCell>
      <TableCell>
        {importedPayorder.recieverName}
        <Typography variant="inherit" color="grey.600">инн: {importedPayorder.recieverInn}</Typography>
        <Typography variant="inherit" color="grey.600">кпп: {importedPayorder.receiverKpp}</Typography>

      </TableCell>
      <TableCell>
        {isRowValid && <GroupCellContent sx={{ maxWidth: 150 }} importedPayorder={importedPayorder} onChange={handleGroupChange} />}
      </TableCell>
      <TableCell>
        <ClippedText text={importedPayorder.paymentDescription} />
      </TableCell>
      <TableCell>
        {importedPayorder.isTransfer
          ? <Typography variant="inherit" color="info.main">Внутренний перевод между своими счетами</Typography>
          : <InvoiceCellContent importedPayorder={importedPayorder} onChange={handleInvoiceChange} readonly/>}
      </TableCell>
      <TableCell>
        <CashflowCellContent
          importedPayorder={importedPayorder}
          onChange={handleCashflowChange}
          readonly={!isRowValid}
          cashflowsDropDown={cashflowsDropDownList}/>

      </TableCell>
      <TableCell>
        <PriceSpan size="small" price={importedPayorder.paymentAmount} />
      </TableCell>
    </TableRow>
  );
};

const ClippedText = ({ text } : {text: string}) => {
  const maxLen = 60;
  if (text.length <= maxLen)
    return <Typography>{text}</Typography>;
  
  return <Tooltip title={text}>
    <Typography>{text.slice(0, maxLen)}...</Typography>
  </Tooltip>;
};

const getRowBgColor = (item: ImportedPayorderDto) => {
  const isValid = !item.invalidReasons;
  if (isValid) return statusColors.warning;

  const reasons = item.invalidReasons;
  if (reasons?.hasDuplicate) return statusColors.success;
  if (reasons?.importEntryAlreadyExists) return statusColors.red;
  if (reasons?.invoiceNotFound) return statusColors.grey;
  return statusColors.grey;
};


export const useRunImportSingle = () => {
  const [loading, setLoading] = useState(false);
  const { showError, showSuccess } = useSnack();

  const runImportSingle = async (importedRowId: number, bankAccountId: number) => {
    setLoading(true);
    return postReq<Pick<RunImportDto, 'bankAccountId'>, PayorderImportReportDto>(
      `accounting/payorders/import/run-import/${importedRowId}`,
      {
        bankAccountId: bankAccountId
      },
      PayorderImportReportDto
    )
      .then(report => {
        showSuccess('Импорт успешно выполнен');
        return report;
      })
      .catch(e => {
        showError('Ошибка: ' + extractNestJsErrorMessage(e));
      })
      .finally(() => setLoading(false));
  };

  return {
    loading,
    runImportSingle
  };
};
